import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import DemoChat from '../components/DemoChat';
import BrandIcon from '../components/BrandIcon';
import { handleTalkToSpecialist } from '../utils/webchat';
import './Health.css';

// ── Agente de demonstração da landing de Saúde ────────────────────────────────
// O agendamento acontece via webchat do próprio agente (mesmo mecanismo do
// Marketplace: /webchat/{tenant}/message + poll). É possível sobrescrever o
// tenant/agente por ambiente via variáveis REACT_APP_HEALTH_DEMO_*.
const DEMO_TENANT = process.env.REACT_APP_HEALTH_DEMO_TENANT || 'fgmax_ltda';
const DEMO_AGENT_ID =
  process.env.REACT_APP_HEALTH_DEMO_AGENT_ID || 'alice_clinica_1784680821619';
const HEALTH_ACCENT = '#0EA5A4'; // teal — paleta clínica

// ── fade-in ao rolar ──────────────────────────────────────────────────────────
function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('visible'); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

// Ícones (simple-icons / emoji) para os pilares e recursos
const PILLAR_ICONS = ['📅', '✅', '📉', '🩹', '💬', '🔗'];
const CHANNEL_ITEMS = [
  { name: 'WhatsApp Business', icon: 'si:whatsapp' },
  { name: 'Webchat no site', icon: '🌐' },
  { name: 'Instagram Direct', icon: 'si:instagram' },
  { name: 'E-mail', icon: '📧' },
  { name: 'Telegram', icon: 'si:telegram' },
  { name: 'Feegow', icon: '🏥' },
];

function HeroSection({ t }) {
  const trust = t('health.hero.trust', { returnObjects: true });
  return (
    <section className="hl-hero">
      <div className="hl-hero-glow" aria-hidden="true" />
      <div className="hl-hero-inner">
        <div className="hl-hero-copy">
          <span className="hl-badge">{t('health.hero.badge')}</span>
          <h1 className="hl-hero-title">
            {t('health.hero.title')}{' '}
            <span className="hl-highlight">{t('health.hero.titleHighlight')}</span>
          </h1>
          <p className="hl-hero-subtitle">{t('health.hero.subtitle')}</p>
          <div className="hl-hero-actions">
            <button type="button" onClick={handleTalkToSpecialist} className="hl-btn-primary">
              {t('health.hero.ctaPrimary')}
            </button>
            <a href="#demo" className="hl-btn-ghost">{t('health.hero.ctaSecondary')} →</a>
          </div>
          {Array.isArray(trust) && (
            <ul className="hl-trust">
              {trust.map((chip, i) => (
                <li key={i}><span className="hl-check" aria-hidden="true">✓</span>{chip}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="hl-hero-visual">
          <DemoChat
            tenant={DEMO_TENANT}
            agentId={DEMO_AGENT_ID}
            name={t('health.demo.agentName')}
            icon="🩺"
            accent={HEALTH_ACCENT}
            welcome={t('health.demo.welcome')}
            placeholder={t('health.demo.placeholder')}
          />
        </div>
      </div>
    </section>
  );
}

function MetricsSection({ t }) {
  const ref = useFadeIn();
  const items = t('health.metrics', { returnObjects: true });
  if (!Array.isArray(items)) return null;
  return (
    <section className="hl-metrics fade-section" ref={ref}>
      <div className="hl-metrics-inner">
        <div className="hl-metrics-header">
          <h2>{t('health.metricsTitle')}</h2>
          <p>{t('health.metricsSubtitle')}</p>
        </div>
        <div className="hl-metrics-grid">
          {items.map((m, i) => (
            <div key={i} className="hl-metric">
              <span className="hl-metric-value">{m.value}</span>
              <span className="hl-metric-label">{m.label}</span>
            </div>
          ))}
        </div>
        <p className="hl-metrics-footnote">{t('health.metricsFootnote')}</p>
      </div>
    </section>
  );
}

function PainSection({ t }) {
  const ref = useFadeIn();
  const items = t('health.pain.items', { returnObjects: true });
  if (!Array.isArray(items)) return null;
  return (
    <section className="hl-section fade-section" ref={ref}>
      <div className="hl-section-inner">
        <div className="hl-section-header">
          <h2>{t('health.pain.title')}</h2>
          <p>{t('health.pain.subtitle')}</p>
        </div>
        <div className="hl-pain-grid">
          {items.map((item, i) => (
            <div key={i} className="hl-pain-card">
              <div className="hl-pain-before">
                <span className="hl-pain-tag hl-pain-tag-before">{t('health.pain.beforeLabel')}</span>
                <p>{item.before}</p>
              </div>
              <div className="hl-pain-arrow" aria-hidden="true">→</div>
              <div className="hl-pain-after">
                <span className="hl-pain-tag hl-pain-tag-after">{t('health.pain.afterLabel')}</span>
                <p>{item.after}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PillarsSection({ t }) {
  const ref = useFadeIn();
  const items = t('health.pillars.items', { returnObjects: true });
  if (!Array.isArray(items)) return null;
  return (
    <section className="hl-section hl-section-tint fade-section" id="recursos" ref={ref}>
      <div className="hl-section-inner">
        <div className="hl-section-header">
          <h2>{t('health.pillars.title')}</h2>
          <p>{t('health.pillars.subtitle')}</p>
        </div>
        <div className="hl-pillars-grid">
          {items.map((item, i) => (
            <div key={i} className="hl-pillar-card">
              <span className="hl-pillar-icon" aria-hidden="true">{PILLAR_ICONS[i] || '•'}</span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ChannelsSection({ t }) {
  const ref = useFadeIn();
  return (
    <section className="hl-section fade-section" ref={ref}>
      <div className="hl-section-inner">
        <div className="hl-section-header">
          <h2>{t('health.channels.title')}</h2>
          <p>{t('health.channels.subtitle')}</p>
        </div>
        <div className="hl-channels">
          {CHANNEL_ITEMS.map((c) => (
            <div key={c.name} className="hl-channel-chip">
              <BrandIcon icon={c.icon} size={20} />
              <span>{c.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StepsSection({ t }) {
  const ref = useFadeIn();
  const items = t('health.steps.items', { returnObjects: true });
  if (!Array.isArray(items)) return null;
  return (
    <section className="hl-section hl-section-tint fade-section" ref={ref}>
      <div className="hl-section-inner">
        <div className="hl-section-header">
          <h2>{t('health.steps.title')}</h2>
          <p>{t('health.steps.subtitle')}</p>
        </div>
        <div className="hl-steps">
          {items.map((step, i) => (
            <div key={i} className="hl-step">
              <span className="hl-step-num">{String(i + 1).padStart(2, '0')}</span>
              <div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DemoSection({ t }) {
  const ref = useFadeIn();
  return (
    <section className="hl-demo fade-section" id="demo" ref={ref}>
      <div className="hl-demo-inner">
        <div className="hl-demo-copy">
          <span className="hl-badge">{t('health.demo.badge')}</span>
          <h2>{t('health.demo.title')}</h2>
          <p>{t('health.demo.subtitle')}</p>
          <ul className="hl-demo-list">
            {(t('health.demo.bullets', { returnObjects: true }) || []).map((b, i) => (
              <li key={i}><span className="hl-check" aria-hidden="true">✓</span>{b}</li>
            ))}
          </ul>
          <p className="hl-demo-note">{t('health.demo.note')}</p>
        </div>
        <div className="hl-demo-chat">
          <DemoChat
            tenant={DEMO_TENANT}
            agentId={DEMO_AGENT_ID}
            name={t('health.demo.agentName')}
            icon="🩺"
            accent={HEALTH_ACCENT}
            welcome={t('health.demo.welcome')}
            placeholder={t('health.demo.placeholder')}
          />
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection({ t }) {
  const ref = useFadeIn();
  const items = t('health.testimonials.items', { returnObjects: true });
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <section className="hl-section hl-section-tint fade-section" ref={ref}>
      <div className="hl-section-inner">
        <div className="hl-section-header">
          <h2>{t('health.testimonials.title')}</h2>
        </div>
        <div className="hl-testimonials">
          {items.map((item, i) => (
            <figure key={i} className="hl-testimonial">
              <blockquote>{item.text}</blockquote>
              <figcaption>
                <span className="hl-testimonial-avatar">{item.name.charAt(0)}</span>
                <span>
                  <strong>{item.name}</strong>
                  <em>{item.role}</em>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection({ t }) {
  const ref = useFadeIn();
  const [open, setOpen] = useState(null);
  const items = t('health.faq.items', { returnObjects: true });
  const toggle = useCallback((i) => setOpen((prev) => (prev === i ? null : i)), []);
  if (!Array.isArray(items)) return null;
  return (
    <section className="hl-section fade-section" ref={ref}>
      <div className="hl-section-inner">
        <div className="hl-section-header"><h2>{t('health.faq.title')}</h2></div>
        <div className="hl-faq">
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className={`hl-faq-item ${isOpen ? 'open' : ''}`}>
                <button className="hl-faq-q" onClick={() => toggle(i)} aria-expanded={isOpen}>
                  {item.question}
                  <span aria-hidden="true">{isOpen ? '−' : '+'}</span>
                </button>
                <div role="region" hidden={!isOpen}>
                  <p className="hl-faq-a">{item.answer}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaSection({ t }) {
  const ref = useFadeIn();
  return (
    <section className="hl-cta fade-section" ref={ref}>
      <div className="hl-cta-inner">
        <h2>{t('health.cta.title')}</h2>
        <p>{t('health.cta.subtitle')}</p>
        <div className="hl-cta-actions">
          <button type="button" onClick={handleTalkToSpecialist} className="hl-btn-primary">
            {t('health.cta.primary')}
          </button>
          <a href="#demo" className="hl-btn-ghost">{t('health.cta.secondary')} →</a>
        </div>
      </div>
    </section>
  );
}

export default function Health() {
  const { t, i18n } = useTranslation();

  const faqItems = t('health.faq.items', { returnObjects: true });
  const faqJsonLd = Array.isArray(faqItems)
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      }
    : null;

  return (
    <div className="hl-page">
      <Helmet>
        <title>{t('health.meta.title')}</title>
        <meta name="description" content={t('health.meta.description')} />
        <meta property="og:title" content={t('health.meta.title')} />
        <meta property="og:description" content={t('health.meta.description')} />
        <link rel="canonical" href="https://libbenagents.com/saude" />
        <html lang={i18n.language} />
        {faqJsonLd && <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>}
      </Helmet>

      <HeroSection t={t} />
      <MetricsSection t={t} />
      <PainSection t={t} />
      <PillarsSection t={t} />
      <ChannelsSection t={t} />
      <StepsSection t={t} />
      <DemoSection t={t} />
      <TestimonialsSection t={t} />
      <FaqSection t={t} />
      <CtaSection t={t} />
    </div>
  );
}
